import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "../lib/api";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

export default function Admin() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [entries, setEntries] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [busyIds, setBusyIds] = useState({});
  const [purging, setPurging] = useState(false);

  const authHeader = () => ({
    headers: { Authorization: "Basic " + btoa(`${username}:${password}`) },
  });

  const load = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const [entriesRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/waitlist`, authHeader()),
        axios.get(`${API}/admin/stats`, authHeader()),
      ]);
      setEntries(entriesRes.data);
      setStats(statsRes.data);
      setAuthed(true);
    } catch (err) {
      toast.error(err?.response?.status === 401 ? "Invalid credentials." : "Failed to load.");
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    try {
      const [entriesRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/waitlist`, authHeader()),
        axios.get(`${API}/admin/stats`, authHeader()),
      ]);
      setEntries(entriesRes.data);
      setStats(statsRes.data);
    } catch {
      toast.error("Failed to refresh.");
    }
  };

  const deleteEntry = async (id, email) => {
    if (!window.confirm(`Delete ${email}?`)) return;
    setBusyIds((b) => ({ ...b, [id]: true }));
    try {
      await axios.delete(`${API}/admin/waitlist/${id}`, authHeader());
      setEntries((prev) => prev.filter((e) => e.id !== id));
      toast.success("Deleted.");
      refresh();
    } catch {
      toast.error("Delete failed.");
    } finally {
      setBusyIds((b) => {
        const { [id]: _, ...rest } = b;
        return rest;
      });
    }
  };

  const purgeTest = async () => {
    if (!window.confirm("Delete all QA/test entries (test@, qa+…, c1@brep.app, @brepp.app)?")) return;
    setPurging(true);
    try {
      const res = await axios.delete(`${API}/admin/waitlist/test`, authHeader());
      toast.success(`Deleted ${res.data.deleted} test entries.`);
      await refresh();
    } catch {
      toast.error("Purge failed.");
    } finally {
      setPurging(false);
    }
  };

  const download = async () => {
    try {
      const res = await axios.get(`${API}/admin/waitlist.csv`, {
        ...authHeader(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "brep-waitlist.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed.");
    }
  };

  return (
    <div className="min-h-screen bg-[#050510] text-white" data-testid="admin-page">
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-20">
        <Link to="/" className="text-[11px] tracking-[0.3em] uppercase text-white/60 hover:text-[#2A5BFF]" data-testid="admin-back">← Back</Link>
        <h1 className="font-anton uppercase text-5xl md:text-6xl mt-8">Admin — Waiting List</h1>
        <div className="mt-3 text-[11px] tracking-[0.3em] uppercase flex flex-wrap gap-6">
          <Link to="/admin/services" className="text-[#2A5BFF] hover:text-white transition-colors" data-testid="admin-services-link">
            → Service Management
          </Link>
          <Link to="/admin/users" className="text-[#2A5BFF] hover:text-white transition-colors" data-testid="admin-users-link">
            → Users &amp; Coaches
          </Link>
          <Link to="/admin/orders" className="text-[#2A5BFF] hover:text-white transition-colors" data-testid="admin-orders-link">
            → Orders &amp; Assignment
          </Link>
          <Link to="/admin/audit-log" className="text-[#2A5BFF] hover:text-white transition-colors" data-testid="admin-audit-log-link">
            → Audit Log
          </Link>
        </div>

        {!authed && (
          <form onSubmit={load} className="mt-10 max-w-md border border-white/10 p-6" data-testid="admin-login">
            <label className="text-[10px] tracking-[0.32em] uppercase text-white/50">Username</label>
            <input
              className="input-line focus-ring"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              data-testid="admin-username"
              required
            />
            <label className="mt-6 block text-[10px] tracking-[0.32em] uppercase text-white/50">Password</label>
            <input
              type="password"
              className="input-line focus-ring"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              data-testid="admin-password"
              required
            />
            <button
              type="submit"
              disabled={loading}
              className="btn-primary mt-8 px-6 py-3 text-[11px] tracking-[0.24em] uppercase disabled:opacity-60"
              data-testid="admin-login-button"
            >
              {loading ? "…" : "Sign In"}
            </button>
          </form>
        )}

        {authed && entries && (
          <div className="mt-10" data-testid="admin-table-wrap">
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-white/10 mb-8" data-testid="admin-stats">
                <StatTile label="Total Views" value={stats.views.total} sub={`${stats.views.today} today`} testid="stat-views-total" />
                <StatTile label="Views · 24h" value={stats.views.last_24h} sub="rolling" testid="stat-views-24h" />
                <StatTile label="Total Signups" value={stats.signups.total} sub={`${stats.signups.today} today`} testid="stat-signups-total" />
                <StatTile label="Coach / Customer" value={`${stats.signups.coach} / ${stats.signups.customer}`} sub="split" testid="stat-signups-split" accent />
              </div>
            )}
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <div className="text-[11px] tracking-[0.28em] uppercase text-white/50">
                {entries.length} entries
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={purgeTest}
                  disabled={purging}
                  className="btn-ghost px-4 py-2 text-[11px] tracking-[0.24em] uppercase disabled:opacity-60"
                  data-testid="admin-purge-test"
                >
                  {purging ? "Purging…" : "Purge Test Entries"}
                </button>
                <button
                  onClick={download}
                  className="btn-ghost px-4 py-2 text-[11px] tracking-[0.24em] uppercase"
                  data-testid="admin-export-csv"
                >
                  Export CSV
                </button>
              </div>
            </div>
            <div className="border border-white/10 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] tracking-[0.28em] uppercase text-white/40 border-b border-white/10">
                  <tr>
                    <th className="p-4 text-left">First Name</th>
                    <th className="p-4 text-left">Email</th>
                    <th className="p-4 text-left">Role</th>
                    <th className="p-4 text-left">Joined</th>
                    <th className="p-4 text-right w-20">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => (
                    <tr key={e.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`admin-row-${e.id}`}>
                      <td className="p-4">{e.first_name}</td>
                      <td className="p-4 text-white/70">{e.email}</td>
                      <td className="p-4">
                        <span className={`text-[10px] tracking-[0.22em] uppercase px-2 py-1 border ${e.role === "coach" ? "border-[#2A5BFF] text-[#2A5BFF]" : "border-white/20 text-white/70"}`}>
                          {e.role}
                        </span>
                      </td>
                      <td className="p-4 text-white/50">{new Date(e.created_at).toLocaleString()}</td>
                      <td className="p-4 text-right">
                        <button
                          onClick={() => deleteEntry(e.id, e.email)}
                          disabled={!!busyIds[e.id]}
                          className="inline-flex items-center justify-center w-8 h-8 border border-white/15 hover:border-red-500 hover:text-red-500 transition-colors disabled:opacity-50"
                          aria-label={`Delete ${e.email}`}
                          data-testid={`admin-delete-${e.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {entries.length === 0 && (
                    <tr>
                      <td colSpan={5} className="p-8 text-center text-white/40 text-sm">No entries yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatTile({ label, value, sub, testid, accent }) {
  return (
    <div
      className={`p-5 md:p-6 border-r border-b md:border-b-0 border-white/10 last:border-r-0 ${accent ? "bg-[#2A5BFF]/[0.06]" : ""}`}
      data-testid={testid}
    >
      <div className="text-[10px] tracking-[0.28em] uppercase text-white/40">{label}</div>
      <div className={`font-anton text-4xl md:text-5xl leading-none mt-3 ${accent ? "text-[#2A5BFF]" : "text-white"}`}>
        {value}
      </div>
      <div className="text-[11px] text-white/40 mt-2 tracking-wide">{sub}</div>
    </div>
  );
}
